import { memo, useState, useCallback, useEffect } from 'react';
import useMediaQuery from '@mui/material/useMediaQuery';
import { Typography, Drawer, Box, Button } from '@mui/material';
import { useParams, useNavigate } from 'react-router-dom';
// Local files
import FilePreview from 'components/Common/FilePreview/FilePreview';
import DrawerSkeleton from 'components/Common/BaseDrawerSkeleton/BaseDrawerSkeleton';
import BaseEventsList from 'components/Analyses/Common/BaseEventsList/BaseEventsList';
import ErrorWrapper from 'components/Common/ErrorWrapper/ErrorWrapper';
import useCustomSelector from 'hooks/useCustomSelector';
import useSecondAnalyses from 'hooks/useSecondAnalyses';
import useEvents from 'hooks/useEvents';
import { eventsSelectors } from "features/events";
import { secondAnalysesSelectors } from 'features/secondAnalyses';

const SecondAnalysisDrawer = () => {
  const mediaDesktop = useMediaQuery(theme => theme.breakpoints.up('md'));
  const navigate = useNavigate();
  const { second_analysis_id } = useParams();
  const { getSecondAnalysis, clearLocalActiveSecondAnalysis } = useSecondAnalyses();
  const { getSecondAnalysesEvents, clearLocalAnalysesEvents } = useEvents();
  const { data, pagination } = useCustomSelector(eventsSelectors.getAnalysesEventsData);
  const eventsFetching = useCustomSelector(eventsSelectors.analysesEventsIsFetching);
  const appointmentAnalysisFetching = useCustomSelector(secondAnalysesSelectors.secondAnalysisIsFetching);
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState(null);
  const [document, setDocument] = useState(null);
  const [result, setResult] = useState(null);
  const [expanded, setExpanded] = useState(false);
  const [firstTimeEventsOpened, setFirstTimeEventsOpened] = useState(true);

  const fetchSecondAnalysis = useCallback((id) => {
    setOpen(true);
    getSecondAnalysis({ id })
    .then(({ payload, error }) => {
      if (!error) {
        const { data: { second_analysis } } = payload;
        handleSetData(second_analysis);
      }
    })
  }, [getSecondAnalysis]);

  const handleSetData = (second_analysis) => {
    setInput(second_analysis?.input);
    setDocument(second_analysis?.document);
    setResult(second_analysis?.result);
  };

  const handleExiting = () => {
    setInput(null);
    setDocument(null);
    setResult(null);
    setExpanded(false);
    setFirstTimeEventsOpened(true);
    navigate(`/flow/fl22n`);
    clearLocalActiveSecondAnalysis();
    clearLocalAnalysesEvents();
  };
  
  const handleClose = () => setOpen(false);

  const fetchSecondAnalysesEvents = useCallback((offset, second_analysis_id) => {
    getSecondAnalysesEvents({ offset, second_analysis_id })
  }, [ getSecondAnalysesEvents]);

  const handleChangeExpand = (_, isExpanded) => {
    setExpanded(isExpanded);
    if (firstTimeEventsOpened) {
      fetchSecondAnalysesEvents(0, second_analysis_id);
      setFirstTimeEventsOpened(false);
    }
  }

  useEffect(() => {
    if (!!second_analysis_id) fetchSecondAnalysis(second_analysis_id);
  }, [second_analysis_id, fetchSecondAnalysis]);

  return (
    <Drawer
      anchor={!mediaDesktop ? 'bottom' : 'right'}
      open={open}
      SlideProps={{ onExited: handleExiting }}
      onClose={handleClose}
      ModalProps={{ sx: { zIndex: 1300 } }}
      PaperProps={{
        sx: {
          height: '100%',
          width: { xs: '100%', md: '600px'},
          backgroundColor: 'background.default'
        }
      }}
    >
      <Box>
        <Box sx={{ display: 'flex', p: '8px 8px 0' }}>
          <Button color='inherit' sx={{ fontWeight: 700 }} onClick={handleClose}>Close</Button>
          <div style={{ flexGrow: 1 }}/>
        </Box>
        <Box sx={{ fontSize: '2rem', fontWeight: 'bold', textAlign: 'center'}}>
            Request log
        </Box>
      </Box>
      <Box sx={{ flexGrow: 1, p: { xs: 2, md: '16px 100px 32px' } }}>
        {appointmentAnalysisFetching ? <DrawerSkeleton /> :
        <>
          <Typography variant='body1' sx={{ fontWeight: 600, textAlign: 'center', mb: '10px' }}>Job Id: {second_analysis_id}</Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center'  }}>
            {input?.id && <Box sx={{ mb: '30px' }}>
              <Typography sx={{ fontSize: '1.5rem', fontWeight: 400, mb: '10px' }}>Input:</Typography>
              <FilePreview
                key={input?.id}
                type={input?.content_type}
                name={input?.filename}
                url={input?.url}
              />
            </Box>}
            {document?.id && <Box sx={{ mb: '30px' }}>
              <Typography sx={{ fontSize: '1.5rem', fontWeight: 400, mb: '10px' }}>Document:</Typography>
              <FilePreview
                key={document?.id}
                type={document?.content_type}
                name={document?.filename}
                url={document?.url}
              />
            </Box>}
            {result?.id && <Box sx={{ mb: '30px' }}>
              <Typography sx={{ fontSize: '1.5rem', fontWeight: 400, mb: '10px' }}>Result:</Typography>
              <FilePreview
                key={result?.id}
                type={result?.content_type}
                name={result?.filename}
                url={result?.url}
              />
            </Box>}
          </Box>
          <BaseEventsList
            data={data}
            pagination={pagination}
            analysisId={second_analysis_id}
            eventsFetching={eventsFetching}
            fetchEvents={fetchSecondAnalysesEvents}
            expanded={expanded}
            handleChangeExpand={handleChangeExpand}
          />
        </>}
      </Box>
    </Drawer>
  );
};

const WrappedComponent = props => {
  return (
    <ErrorWrapper>
      <SecondAnalysisDrawer {...props} />
    </ErrorWrapper>
  );
};

export default memo(WrappedComponent);